// gearGeometry.js
// Pure geometry helpers for building gears as SVG paths.
// No React, no side effects — safe to memoize results in components.

const TAU = Math.PI * 2;

const polar = (cx, cy, r, a) => [cx + r * Math.cos(a), cy + r * Math.sin(a)];

const fmt = (n) => Math.round(n * 1000) / 1000;

/**
 * Generate a closed path string for a gear's toothed outer ring.
 *
 * @param {number} cx      centre x
 * @param {number} cy      centre y
 * @param {number} rTip    radius at the tip of a tooth (outer-most)
 * @param {number} rRoot   radius at the valley between teeth
 * @param {number} teeth   number of teeth
 * @returns {string} SVG path `d`
 */
export function gearTeethPath(cx, cy, rTip, rRoot, teeth) {
  const step = TAU / teeth;
  // Angular layout inside one tooth pitch (fractions of `step`):
  // 0.00 valley start | 0.18 rise to tip | 0.40 tip end | 0.58 fall to valley | 1.00 next
  const a1 = 0.18;
  const a2 = 0.40;
  const a3 = 0.58;

  let d = '';
  for (let i = 0; i < teeth; i++) {
    const base = i * step - step * 0.5;
    const pRootIn = polar(cx, cy, rRoot, base);
    const pRise = polar(cx, cy, rTip, base + step * a1);
    const pTip = polar(cx, cy, rTip, base + step * a2);
    const pFall = polar(cx, cy, rRoot, base + step * a3);

    if (i === 0) d += `M ${fmt(pRootIn[0])} ${fmt(pRootIn[1])} `;
    else d += `L ${fmt(pRootIn[0])} ${fmt(pRootIn[1])} `;
    d += `L ${fmt(pRise[0])} ${fmt(pRise[1])} `;
    d += `L ${fmt(pTip[0])} ${fmt(pTip[1])} `;
    d += `L ${fmt(pFall[0])} ${fmt(pFall[1])} `;
  }
  return d + 'Z';
}

/**
 * Generate tapered mechanical spokes between the hub and the inner ring.
 *
 * @param {number} cx
 * @param {number} cy
 * @param {number} rHub     outer radius of the centre hub
 * @param {number} rInner   inner radius of the surrounding ring
 * @param {number} count    number of spokes
 * @param {number} hubWidth angular half-width at the hub (radians)
 * @param {number} outWidth angular half-width at the ring (radians)
 * @returns {string} SVG path `d` containing every spoke
 */
export function gearSpokesPath(cx, cy, rHub, rInner, count, hubWidth = 0.20, outWidth = 0.12) {
  const step = TAU / count;
  let d = '';
  for (let i = 0; i < count; i++) {
    const a = i * step;
    const p1 = polar(cx, cy, rHub, a - hubWidth);
    const p2 = polar(cx, cy, rInner, a - outWidth);
    const p3 = polar(cx, cy, rInner, a + outWidth);
    const p4 = polar(cx, cy, rHub, a + hubWidth);
    d += `M ${fmt(p1[0])} ${fmt(p1[1])} `;
    d += `L ${fmt(p2[0])} ${fmt(p2[1])} `;
    d += `L ${fmt(p3[0])} ${fmt(p3[1])} `;
    d += `L ${fmt(p4[0])} ${fmt(p4[1])} Z `;
  }
  return d.trim();
}

/**
 * Evenly spaced detail points on a circle (used for bolt holes / lightening holes).
 *
 * @returns {Array<{x:number,y:number}>}
 */
export function ringPoints(cx, cy, r, count, phase = 0) {
  const step = TAU / count;
  const out = [];
  for (let i = 0; i < count; i++) {
    const [x, y] = polar(cx, cy, r, phase + i * step);
    out.push({ x: fmt(x), y: fmt(y) });
  }
  return out;
}
